
import { CategoryInfo } from "./CategoryInfo";
import { FieldInfo } from "./FieldInfo";
import { ProductFieldInfo } from "./ProductFieldInfo";
 
import { ProductFieldInfoVisibility } from "./ProductFieldInfoVisibility";

/** Value for a GFFP xmlTag from PostValidation root (case-insensitive) or from FieldValues / fieldValues pairs. */
function getValidationRequestValueForTag(validationReq: any, tagLc: string): unknown {
	if (!validationReq || typeof validationReq !== "object" || !tagLc) return undefined;

	for (const key of Object.keys(validationReq)) {
		if (key.toLowerCase() === tagLc) {
			return (validationReq as any)[key];
		}
	}

	const fv = (validationReq as any).FieldValues ?? (validationReq as any).fieldValues;
	if (!Array.isArray(fv)) return undefined;
	for (const pair of fv) {
		const k = String(pair?.key ?? pair?.Key ?? "").trim().toLowerCase();
		if (k === tagLc) {
			return pair?.value ?? pair?.Value;
		}
	}
	return undefined;
}

export class RequiredFields {
  public categories?: CategoryInfo[];

  constructor() {
    this.categories = [];
  }

  /**
   * Get true if the sender’s legal ID is required (this is different from photo ID, usually SSN); otherwise false.
   */
  public get isSendersLegalIdRequired(): boolean {
    const fieldInfo = this.getField("senderLegalIdNumber");
    if (fieldInfo) {
      return fieldInfo.visibility === ProductFieldInfoVisibility.REQ;
    }
    return false;
  }

  /**
   * Gets the field that matches the provided tag name.
   * @param tagName The name of the field to look for.
   * @returns A FieldInfo object that matches the provided tag name or null.
   */
  public getField(tagName: string): FieldInfo | null {
    if (!tagName) {
      return null;
    }

    for (const cat of this.categories ?? []) {
      for (const field of cat.fields ?? []) {
        if (field.tagName?.toLowerCase() === tagName.toLowerCase()) {
          return field;
        }
      }
    }
    return null;
  }

  /**
   * Gets the category that matches the specified category name.
   * @param categoryName The name of the category.
   * @returns A CategoryInfo object that matches the provided name or null.
   */
  public getCategory(categoryName: string): CategoryInfo | null {
    if (this.categories && categoryName?.trim()) {
      return (
        this.categories.find(
          (x) =>
            x.categoryName?.toLowerCase() === categoryName.trim().toLowerCase()
        ) ?? null
      );
    }
    return null;
  }

  public getMissingFields(gffpFieldsByCategory: Record<string, any[]>, validationReq: any): any[] {
    const missingFields: ProductFieldInfo[] = [];

    for (const category of Object.values(gffpFieldsByCategory)) {
      for (const field of category) {
        if (field.visibility === ProductFieldInfoVisibility.REQ) {
          const rawTag: string = String(field?.XmlTag ?? field?.xmlTag ?? '').trim();
          const tagLc = rawTag.toLowerCase();
          const value = getValidationRequestValueForTag(validationReq, tagLc);
          if (!value) {
            missingFields.push(field);
          }
        }
      }
    }
    return missingFields;
  }

  /**
   * Bill Pay specific: compute missing required fields from a flat list
   * of fields containing XmlTag/xmlTag and Visibility/visibility properties.
   */
  public getMissingFieldsForBP(fieldsFlat: any[] | null | undefined, validationReq: any): any[] {
    const missingFields: any[] = [];

    if (!Array.isArray(fieldsFlat) || fieldsFlat.length === 0) {
      return missingFields;
    }

    for (const field of fieldsFlat) {
      const visibility = field?.Visibility ?? field?.visibility;
      const isRequired = visibility === ProductFieldInfoVisibility.REQ || visibility === 0;
      if (!isRequired) continue;

      const rawTag: string = String(field?.XmlTag ?? field?.xmlTag ?? '').trim();
      if (!rawTag) {
        // If tag is missing, treat as missing to be safe
        missingFields.push(field);
        continue;
      }
      const tagLc = rawTag.toLowerCase();
      const value = getValidationRequestValueForTag(validationReq, tagLc);
      if (!value) {
        missingFields.push(field);
      }
    }

    return missingFields;
  }
}
